/* Clipboard destination. Successor to ClipDock, which had to be a collapsed
 * bar first and a panel second; as a destination there is no collapsed state,
 * so the layout is just a column that fills its share of the shell.
 *
 * 2026-08-03 redesign (R1/R6): the composer is docked to the bottom — RECENT
 * scrolls above it inside `.list`, `.composer` stays `flex: 0 0 auto` so it
 * never moves. `.column` caps the content at a reading width and centres it
 * on wide (desktop) viewports; the list and composer share that edge because
 * they're both children of it. */

.screen {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-12) var(--spacing-8);
  /* Fills the destination area so `.list` — not the screen — is what
   * scrolls when there are many clips. */
  flex: 1;
  min-height: 0;
}

.column {
  display: flex;
  flex-direction: column;
  /* Section rhythm 12/4: RECENT→list is pulled to 4px by .recentHeader's
   * negative margin below (so the label hugs its rows); list→composer stays
   * a full 12px, matching the composer's own top hairline gap. */
  gap: var(--spacing-12);
  flex: 1;
  min-height: 0;
  width: 100%;
  /* Desktop cap (R6): the void below a single clip at desktop width was
   * defect #1 in the redesign spec. Capping the column (not the screen)
   * keeps the padding above symmetric at narrow widths. */
  max-width: 480px;
  margin: 0 auto;
}

/* Wraps the RECENT heading + the clear-all control on one row. The negative
 * margin nets the header→list gap in `.column` (12px) down to 4px, so the
 * label hugs its rows rather than floating as a free row of its own. */
.recentHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-8);
  margin-bottom: calc(var(--spacing-4) - var(--spacing-12));
}

.recentHeading {
  letter-spacing: 0.08em;
}

.clearAllConfirm {
  display: flex;
  gap: var(--spacing-8);
}

.list {
  display: flex;
  flex-direction: column;
  /* ClipCard rows are cards now (2026-08-05 card-language redesign, C1) —
   * no hairline divider to double up with — so they need a real gap
   * between them, same rhythm as the notification list's `.list`. Day-group
   * headers (C9, below) are direct children of this flex column too, so the
   * same gap also separates a header from its first card and from the
   * previous group's last card — deliberately equal, not a bigger seam:
   * the header text itself already reads as the group boundary. */
  gap: var(--spacing-8);
  /* The scrolling region. `min-height: 0` is required for a flex child to
   * shrink below its content and actually scroll (Safari's implicit
   * `min-height: auto` otherwise defeats overflow-y). */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* Day-group section header (C9) — one rendered above each `groupByDay`
 * group ("Today" / "Yesterday" / an older formatted date). Styled to match
 * `SidePanelNotificationList`'s `.dateLabel` ("Today" section label) so the
 * two lists read as the same product: same size/weight/tone (via `Text
 * size="xs" tone="tertiary" weight="medium"`), same letter-spacing and
 * no-wrap here. No `animation: fadeIn` here (unlike `.dateSeparator`) —
 * chrome to match, not motion; this rewrite doesn't add a new opt-in
 * animation for a header repaint. */
.dayHeader {
  display: flex;
  align-items: center;
  padding: var(--spacing-4) 0;
}

.dayLabel {
  letter-spacing: 0.12px;
  white-space: nowrap;
}

/* Designed empty state (R7): a single quiet line read as broken at desktop
 * height. Fills `.list`'s flex box so the title + body centre in whatever
 * vertical space the destination has, rather than hugging the top. */
.empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-8);
  padding: var(--spacing-40) var(--spacing-16);
  text-align: center;
}

/* Docked (R1): pinned to the bottom of the destination, never resized or
 * collapsed by any event (see the Android keyboard-bar-bounce lesson in the
 * plan's global constraints — the fix there was to stop auto-resizing, not
 * to add another one here). The top hairline separates it from the scrolling
 * list above. */
.composer {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: var(--spacing-8);
  flex: 0 0 auto;
  border-top: 1px solid var(--stroke-neutral);
  padding-top: var(--spacing-12);
}

.composerField {
  flex: 1;
  min-width: 0;
}
